/**
 * 费用录入表单组件
 * 用户录入费用信息并选择分摊方式
 *
 * 支持两种分摊方式：
 *   - EQUAL_SPLIT  平均分摊：录入「原币金额」，按航段数平均拆分
 *   - BY_BLOCK_HOURS  BH×单价：录入「每BH单价」，各航段费用 = 该航段BH × 单价
 *
 * 两种方式都支持非 USD 币种，按汇率换算成 USD（金额或单价）。
 * 注意：BH×单价模式下「总额」依赖选中航段的总 BH，无法在本表单内算出，
 *       因此本表单只负责输出单价与汇率，总额由父页面结合选中航段计算。
 */

import React, { useState, useEffect } from "react";
import { Form, Select, Checkbox, InputNumber, message, Spin } from "antd";
import { contractManagementService } from "../../../../api/enhancedApi";
import { useCurrencies } from "../../../../hooks/useCurrencies";

/**
 * 分摊方式选项
 */
export const ALLOCATION_METHOD = {
    EQUAL_SPLIT: "EQUAL_SPLIT",
    BY_BLOCK_HOURS: "BY_BLOCK_HOURS",
};

const ALLOCATION_METHOD_OPTIONS = [
    { value: ALLOCATION_METHOD.EQUAL_SPLIT, label: "平均分摊（总金额 ÷ 航段数）" },
    { value: ALLOCATION_METHOD.BY_BLOCK_HOURS, label: "BH × 单价（各航段 BH × 单价）" },
];

/**
 * 费用类型选项（基于旧Java系统的15种标准费用类型）
 * 格式：英文代码-中文描述
 */
const COST_TYPE_OPTIONS = [
    { value: 'bh', label: 'bh-BH时间' },
    { value: 'fuel', label: 'fuel-燃油费' },
    { value: 'landing_parking', label: 'landing_parking-起降停场费' },
    { value: 'ground_handling', label: 'ground_handling-地面服务费' },
    { value: 'catering', label: 'catering-配餐费' },
    { value: 'transportation', label: 'transportation-交通费' },
    { value: 'deicing', label: 'deicing-除冰费' },
    { value: 'navigation', label: 'navigation-导航费' },
    { value: 'positioning', label: 'positioning-调机费' },
    { value: 'laundry', label: 'laundry-洗涤费' },
    { value: 'accommodation', label: 'accommodation-住宿费' },
    { value: 'disinfection', label: 'disinfection-消毒费' },
    { value: 'flight_service_charge', label: 'flight_service_charge-飞行服务费' },
    { value: 'uld', label: 'uld-ULD租赁费' },
    { value: 'traffic_rights_permit', label: 'traffic_rights_permit-航权及许可费' },
    { value: 'sita', label: 'sita-SITA费' },
    { value: 'commission', label: 'commission-佣金费' },
    { value: 'acmi', label: 'acmi-ACMI费用' },
    { value: 'cargo_handling', label: 'cargo_handling-货物操作费' },
    { value: 'traffic_rights', label: 'traffic_rights-航权费' },
    { value: 'trucking_fee', label: 'trucking_fee-卡车运输费' },
    { value: 'others_fee', label: 'others_fee-其他费用' }
];

// 币种下拉统一改用组件内 useCurrencies()（后端 COMMON_CURRENCIES），此处不再维护硬编码列表


/**
 * 费用录入表单组件
 */
const CostInfoForm = ({ onCostChange }) => {
    const [form] = Form.useForm();
    const { currencyOptions } = useCurrencies();

    // 费用录入数据（amount 用于平均分摊；unitPrice 用于 BH×单价）
    const [costData, setCostData] = useState({
        allocationMethod: ALLOCATION_METHOD.EQUAL_SPLIT,
        supplier: undefined,
        costType: undefined,
        amount: 0.01,
        unitPrice: 0.01,
        currency: 'USD',
        isEstimate: false
    });

    // 供应商数据状态
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);

    const isBhMode = costData.allocationMethod === ALLOCATION_METHOD.BY_BLOCK_HOURS;

    // 组件初始化时获取供应商数据
    useEffect(() => {
        loadSuppliers();
    }, []);

    // 统一出口：费用信息变化时通知父组件。
    // 注意：本表单只输出「原币」口径（金额/单价/币种），不做任何汇率换算。
    // 各段美元由父页面结合选中航段的航班日汇率统一计算（预览与提交共用同一套）。
    useEffect(() => {
        onCostChange && onCostChange({ ...costData });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costData]);

    // 获取供应商列表。走可选供应商口径：暂停的供应商由服务端过滤，不再按启用筛
    const loadSuppliers = async () => {
        try {
            setSuppliersLoading(true);

            const response = await contractManagementService.businessPartner.getSuppliers({
                page: 1,
                page_size: 500,
            });

            if (response && response.data && response.data.items) {
                const suppliers = response.data.items.map(supplier => ({
                    value: supplier.id,
                    label: supplier.name || supplier.code,
                    name: supplier.name,
                    code: supplier.code,
                    ...supplier
                }));
                setSupplierOptions(suppliers);
            } else {
                message.warning("获取供应商数据格式异常");
                setSupplierOptions([]);
            }
        } catch (error) {
            message.error("获取供应商列表失败: " + error.message);
            setSupplierOptions([]);
        } finally {
            setSuppliersLoading(false);
        }
    };

    // 处理表单字段变化
    const handleFieldChange = (field, value) => {
        setCostData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div>
            <Form form={form} layout="vertical" size="small">
                {/* 分摊方式 */}
                <Form.Item label="分摊方式" required>
                    <Select
                        value={costData.allocationMethod}
                        onChange={(value) => handleFieldChange('allocationMethod', value)}
                        options={ALLOCATION_METHOD_OPTIONS}
                    />
                </Form.Item>

                {/* 供应商 */}
                <Form.Item
                    label="供应商"
                    required
                >
                    <Select
                        placeholder={suppliersLoading ? "加载中..." : "请选择供应商"}
                        value={costData.supplier}
                        onChange={(value) => handleFieldChange('supplier', value)}
                        options={supplierOptions}
                        loading={suppliersLoading}
                        showSearch
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ||
                            option?.name?.toLowerCase().includes(input.toLowerCase())
                        }
                        notFoundContent={suppliersLoading ? <Spin size="small" /> : "暂无供应商数据"}
                        disabled={suppliersLoading}
                    />
                </Form.Item>

                {/* 费用类型 */}
                <Form.Item
                    label="费用类型"
                    required
                >
                    <Select
                        placeholder="请选择费用类型"
                        value={costData.costType}
                        onChange={(value) => handleFieldChange('costType', value)}
                        options={COST_TYPE_OPTIONS}
                        showSearch
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>

                {/* 原币金额（仅平均分摊） */}
                {!isBhMode && (
                    <Form.Item
                        label="原币金额"
                        required
                    >
                        <InputNumber
                            value={costData.amount}
                            onChange={(value) => handleFieldChange('amount', value || 0)}
                            placeholder="0.01"
                            min={0}
                            step={0.01}
                            precision={2}
                            controls={true}
                            style={{ width: '100%' }}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>
                )}

                {/* 单价（仅 BH×单价） */}
                {isBhMode && (
                    <Form.Item
                        label="单价（每 BH 单价）"
                        required
                        help="各航段费用 = 该航段 BH × 单价"
                    >
                        <InputNumber
                            value={costData.unitPrice}
                            onChange={(value) => handleFieldChange('unitPrice', value || 0)}
                            placeholder="0.01"
                            min={0}
                            step={0.01}
                            precision={2}
                            controls={true}
                            style={{ width: '100%' }}
                            addonAfter={`${costData.currency}/h`}
                        />
                    </Form.Item>
                )}

                {/* 币种 */}
                <Form.Item
                    label="币种"
                    required
                >
                    <Select
                        value={costData.currency}
                        onChange={(value) => handleFieldChange('currency', value)}
                        options={currencyOptions}
                    />
                </Form.Item>

                {/* 是否估算 */}
                <Form.Item>
                    <Checkbox
                        checked={costData.isEstimate}
                        onChange={(e) => handleFieldChange('isEstimate', e.target.checked)}
                    >
                        是否估算
                    </Checkbox>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                        勾选表示此费用为您估算
                    </div>
                </Form.Item>

            </Form>
        </div>
    );
};

export default CostInfoForm;
